'use client';
import React from 'react';
import { IconSearch, IconDeleteCircle } from '@repo/svgs';
import { cn } from '@/lib/cn';

export interface SearchInputPillProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'className'> {
  onValueChange?: (value: string) => void;
  inputRef?: React.Ref<HTMLInputElement>;
  onClear?: () => void;
  isLoading?: boolean;
  className?: string;
  showClearButton?: boolean;
}

export function SearchInputPill({
  value,
  onValueChange,
  placeholder,
  disabled,
  className,
  inputRef,
  onClear,
  readOnly,
  isLoading = false,
  showClearButton = true,
  ...rest
}: SearchInputPillProps) {
  const [hasMounted, setHasMounted] = React.useState(false);
  React.useEffect(() => {
    setHasMounted(true);
  }, []);
  const stringValue = String(value ?? '');
  const hasValue = stringValue.length > 0;
  const isClearButtonVisible = showClearButton && hasValue && (onValueChange || onClear);
  const shouldRenderClearButton = isClearButtonVisible && hasMounted && !isLoading;
  const shouldRenderRightSlot = (shouldRenderClearButton || isLoading) && hasMounted;

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onValueChange?.(event.target.value);
  };

  const handleClear = () => {
    if (onClear) {
      onClear();
    } else {
      onValueChange?.('');
    }
  };

  return (
    <div
      className={cn(
        // Layout
        'flex w-auto items-center gap-2',
        // Pill appearance
        'rounded-full border border-secondary bg-primary px-4 py-1',
        // Hover state - entire component gets hover background
        'hover:bg-primary-hover transition-colors',
        // Active state (focused)
        'shadow focus-within:shadow-none transition-shadow',
        shouldRenderRightSlot ? 'pr-1' : 'pr-4',
        // State
        disabled && 'opacity-60',
        // Allow caller override
        className,
      )}
    >
      <IconSearch className={cn('icon-tertiary', 'shrink-0')} width={16} height={16} />

      <input
        ref={inputRef}
        type="text"
        value={stringValue}
        onChange={handleChange}
        placeholder={placeholder}
        disabled={disabled}
        readOnly={readOnly ?? !onValueChange}
        className={cn(
          // Sizing/layout
          'min-w-0 flex-1 py-2',
          // Appearance - transparent so it inherits parent hover
          'bg-transparent text-body-2 text-primary',
          // Placeholder
          'placeholder:text-tertiary',
          // Misc
          'outline-none',
        )}
        {...rest}
      />

      {isLoading && hasMounted ? (
        <div className="inline-flex h-9 w-9 items-center justify-center rounded-full p-2">
          <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      ) : null}

      {shouldRenderClearButton && (
        <button
          type="button"
          aria-label="Clear search"
          onClick={handleClear}
          disabled={disabled}
          className={cn(
            // Size/layout
            'inline-flex h-9 w-9 items-center justify-center rounded-full p-2',
            // Appearance
            'cursor-pointer',
            // Disabled
            'disabled:cursor-default disabled:opacity-0',
          )}
        >
          <IconDeleteCircle className="icon-secondary" width={16} height={16} />
        </button>
      )}
    </div>
  );
}

export default SearchInputPill;
